import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Boxes } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import jsyaml from 'js-yaml'
import { useBuilderObjectType } from './hooks/useBuilderObjectTypes'
import { useBuilderObject } from './hooks/useBuilderObjects'
import { FieldRow } from './editors/FieldRow'
import { FieldEditorPopover } from './editors/FieldEditorPopover'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'
// Amorce le registre des éditeurs (text, image-drupal, lien, liste…) : la
// modale s'ouvre depuis l'écran d'un site, sans être passée par le builder.
import './editors'

/**
 * Créer un CONTENU d'un type de contenu, sans quitter l'écran d'où on part.
 *
 * Même surface que le formulaire de `BuilderObjectEditor` — les champs du
 * schéma du type, rendus par le même `FieldRow` et la même pop-in ancrée —
 * mais en pop-in : la création est un geste qu'on fait DEPUIS un écran, elle
 * n'est pas une destination (c'est la règle déjà tenue par
 * `BuilderPageCreateMount` pour les pages). L'écran plein reste le lieu de
 * l'ÉDITION : c'est là qu'on arrive une fois le contenu créé.
 *
 * POURQUOI DU JSX ET PAS DU YAML (rituel config-first) : les champs à saisir
 * ne sont pas connus à l'avance — ils viennent du schéma YAML du type, lu à
 * l'exécution, et chacun choisit son éditeur dans un registre. Aucune capacité
 * reactive-json ne rend un formulaire dont la forme arrive avec la donnée. On
 * réutilise donc les pièces de l'éditeur d'objet plutôt que d'en écrire un
 * second : `FieldRow`, `FieldEditorPopover`, `useBuilderObjectType`,
 * `useBuilderObject`.
 *
 * Props :
 *  - objectType : le nom machine du type (`emerya_article`…).
 *  - siteId     : le site où faire NAÎTRE le contenu. Le back le prendrait par
 *                 défaut sur le type ; le passer dit l'intention.
 *  - onClose    : l'appelant referme (il porte l'état d'ouverture).
 */
export const BuilderObjectCreateModal = ({ objectType, siteId = null, onClose }) => {
  const scrim = useScrimDismiss(onClose)
  const navigate = useNavigate()
  const titleId = useId()

  const { type, loading: typeLoading, error: typeError } = useBuilderObjectType(objectType)
  // `null` : rien à charger, on crée. Seul `saveObject` sert ici.
  const { saveObject } = useBuilderObject(objectType, null)

  const parsedDef = useMemo(() => {
    if (!type?.definition) return null
    try {
      return jsyaml.load(type.definition) || {}
    } catch {
      return null
    }
  }, [type?.definition])

  const fields = useMemo(() => parsedDef?.fields || [], [parsedDef])

  const [data, setData] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  // {key, anchorEl} du champ dont la pop-in ancrée est ouverte.
  const [modal, setModal] = useState(null)

  // Les valeurs par défaut du schéma, dès que le type est là : un contenu neuf
  // part de ce que son type déclare, comme dans l'éditeur plein écran.
  useEffect(() => {
    if (parsedDef) setData(parsedDef.defaultData || {})
  }, [parsedDef])

  useEscapeClose(onClose)

  // Le focus entre dans la pop-in dès qu'elle a ses champs : le bouton qui
  // vient de l'ouvrir vivait dans un menu qui se referme au même instant, et le
  // focus retomberait sur <body> — plus rien à tabuler, rien à annoncer
  // (RGAA 12.6). Le premier champ du schéma l'accueille ; sans champ (ou le
  // temps du chargement), le panneau lui-même le prend.
  const formRef = useRef(null)
  useEffect(() => {
    if (typeLoading) return
    const root = formRef.current
    if (!root) return
    const first = root.querySelector('.modal-body input, .modal-body textarea, .modal-body button')
    ;(first ?? root).focus()
  }, [typeLoading])

  const setField = (key, value) => setData(prev => ({ ...prev, [key]: value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (saving || !parsedDef) return
    setSaving(true)
    setError(null)
    try {
      // Pas de slug saisi : le back le dérive du champ primaire, et l'ajuste
      // s'il est déjà pris. C'est l'écran d'édition qui permettra de le revoir.
      const result = await saveObject({ data, siteId: siteId || null })
      // Pas de `onClose()` : la navigation démonte l'écran qui porte la modale.
      // `onClose` ne veut donc dire que « l'utilisateur a renoncé ».
      navigate(`/builder/types/${objectType}/instance/${result.slug || result.nid}`)
    } catch (err) {
      setError(err?.message || 'Une erreur est survenue. Réessayez.')
    } finally {
      setSaving(false)
    }
  }

  const popover = modal && (() => {
    const field = fields.find(f => f.key === modal.key)
    if (!field) return null
    return (
      <FieldEditorPopover
        anchorEl={modal.anchorEl}
        field={field}
        value={data[field.key]}
        onSave={(key, v) => {
          setField(key, v)
          setModal(null)
        }}
        onClose={() => setModal(null)}
      />
    )
  })()

  return (
    <div className="modal-scrim" {...scrim}>
      <form
        ref={formRef}
        tabIndex={-1}
        className="modal-panel w-full max-w-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="modal-header">
          <h2 id={titleId}>
            Créer un contenu{type?.name ? ` — ${type.name}` : ''}
          </h2>
        </div>

        <div className="modal-body space-y-5">
          {typeLoading && (
            <p className="text-sm text-slate-500" role="status">Chargement du type…</p>
          )}

          {!typeLoading && (typeError || !type || !parsedDef) && (
            <p role="alert" className="text-sm text-rose-600">
              Type «&nbsp;{objectType}&nbsp;» introuvable, ou sa définition YAML est invalide.
            </p>
          )}

          {!typeLoading && parsedDef && (
            <>
              <p className="flex items-center gap-2 rounded-lg border border-iris-100 bg-iris-50 px-3 py-2 text-xs text-iris-700">
                <Boxes size={14} className="shrink-0" aria-hidden="true" />
                <span>
                  Ce contenu naîtra dans le type <strong>{type.name || objectType}</strong>
                  {type.siteName ? <> du site <strong>{type.siteName}</strong></> : null}.
                </span>
              </p>

              {fields.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">
                  Aucun champ déclaré dans le schéma de ce type.
                </p>
              )}
              {fields.map((field) => (
                <FieldRow
                  key={field.key}
                  field={field}
                  value={data[field.key]}
                  onChange={(v) => setField(field.key, v)}
                  onOpenModal={(e) => setModal({ key: field.key, anchorEl: e?.currentTarget ?? null })}
                />
              ))}
            </>
          )}

          {error && <p role="alert" className="text-xs text-rose-600">{error}</p>}
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={saving || typeLoading || !parsedDef}>
            {saving ? 'Création…' : 'Créer le contenu'}
          </button>
        </div>
      </form>

      {/* Éditeur ancré au champ (image, lien, liste…) : même surface que dans
          l'éditeur plein écran. Rendu en portail sur <body>, au-dessus du
          scrim (z-index du DS). */}
      {popover}
    </div>
  )
}

export default BuilderObjectCreateModal
